<script setup lang="ts">
import { ref, toRefs, Ref, computed, watch, onBeforeMount } from "vue";
import { useI18n } from "vue-i18n";
import SecondaryDrawerHeader from "@/modules/explorer/components/SecondaryDrawer/SecondaryDrawerHeader.vue";
import { useSegmentBuilderStore } from "@/modules/segments/store";
const segmentBuilderState = useSegmentBuilderStore().segmentBuilderState;
import SegmentPartDO = App.Data.Segments.SegmentPartDO;
import { largerThan, required } from "@/ui/validation/validationRules";
import { ucfirst } from "chartjs-graph";
const { t } = useI18n();

const emit = defineEmits(["close-filter-bar"]);

const availableOperators = [
	{ name: "Equals", operator: "=" },
	{ name: "Larger than", operator: ">" },
	{ name: "Less than", operator: "<" },
	{ name: "Between", operator: "between" },
];

const props = defineProps<{
	filterPartType: string;
}>();

const { filterPartType } = toRefs(props);

const localPartCopy: Ref<SegmentPartDO> = ref();

const partToEdit = computed({
	get() {
		return segmentBuilderState.segment.parts.find(
			(part) =>
				part.data_source === "tab_occ_prk_v" &&
				part.type === filterPartType.value
		);
	},
	set(newPart) {
		const index = segmentBuilderState.segment.parts.findIndex(
			(part) =>
				part.data_source === "tab_occ_prk_v" &&
				part.type === filterPartType.value
		);
		if (index !== -1) {
			segmentBuilderState.segment.parts[index] = { ...newPart };
		}
	},
});

const copyPartToLocalCopy = () => {
	const values =
		partToEdit.value?.values && partToEdit.value?.values.length === 2
			? [...partToEdit.value.values]
			: [0, 0];

	localPartCopy.value = { ...partToEdit.value, values };
};

const applyFilter = () => {
	partToEdit.value = {
		...localPartCopy.value,
		...{
			values: [
				Number(localPartCopy.value.values[0]),
				Number(localPartCopy.value.values[1]),
			],
		},
	};
	emit("close-filter-bar");
};

watch(partToEdit, () => {
	copyPartToLocalCopy();
});

onBeforeMount(() => {
	copyPartToLocalCopy();
});

const activeTab: Ref<string> = ref("summary_condition");
const isFormValid: Ref<boolean> = ref(false);
</script>

<template>
	<div class="d-flex flex-column h-100">
		<div class="flex-grow-0">
			<SecondaryDrawerHeader icon="mdi-group" title="Summary condition" />
		</div>

		<v-window v-model="activeTab" class="flex-grow-1">
			<v-window-item
				key="summary_condition"
				value="summary_condition"
				class="h-100"
			>
				<v-form v-model="isFormValid">
					<div class="d-flex flex-column h-100">
						<div class="flex-shrink-1 overflow-y-auto">
							<div class="pa-5">
								<div>
									<h6 class="text-h6 mb-3">Metric:</h6>
									<v-autocomplete
										placeholder="Select a Specification"
										:items="segmentBuilderState.availableMetrics"
										:item-title="
											(property) =>
												ucfirst(
													t(`segments.metrics.${property.name_clean}.action`)
												)
										"
										item-value="id"
										return-object
										v-model="localPartCopy.metric"
										:rules="[required(t)]"
									>
									</v-autocomplete>
								</div>
								<div>
									<h6 class="text-h6 mb-3">Operator:</h6>
									<v-autocomplete
										placeholder="Select a Specification"
										:items="availableOperators"
										item-title="name"
										item-value="operator"
										v-model="localPartCopy.operator"
										:rules="[required(t)]"
									>
									</v-autocomplete>
								</div>
								<div>
									<h6 class="text-h6 mb-3">Quantity:</h6>
									<div class="d-flex w-100">
										<div class="w-100">
											<v-text-field
												type="number"
												hide-details="auto"
												variant="filled"
												placeholder="from"
												v-model="localPartCopy.values[0]"
												:rules="[required(t)]"
											></v-text-field>
										</div>
										<div
											class="w-100 d-flex justify-center"
											v-if="localPartCopy.operator === 'between'"
										>
											<div class="pt-3">and</div>
										</div>
										<div
											class="w-100"
											v-if="localPartCopy.operator === 'between'"
										>
											<v-text-field
												type="number"
												hide-details="auto"
												variant="filled"
												placeholder="from"
												v-model="localPartCopy.values[1]"
												:rules="[
													required(t),
													largerThan(t, localPartCopy.values[0]),
												]"
											></v-text-field>
										</div>
									</div>
								</div>
								<div class="d-flex pt-5">
									<v-spacer />
									<v-btn
										color="primary"
										text="Apply"
										:disabled="isFormValid === false"
										@click="applyFilter()"
									></v-btn>
								</div>
							</div>
						</div>
					</div>
				</v-form>
			</v-window-item>
		</v-window>
	</div>
</template>

<style scoped lang="scss"></style>
